import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  clearMfaPending,
  clearState,
  resendMfaOtp,
  selectAuthErrorMessage,
  selectAuthIsFetching,
  selectMfaPending,
  verifyMfa
} from "features/auth/auth-slice";

const RESEND_COOLDOWN_SECONDS = 60;

type VerifyMode = "code" | "backup" | "bypass";

const MODE_CONFIG: Record<
  VerifyMode,
  { label: string; maxLength: number; buttonLabel: string }
> = {
  code: { label: "6-digit code", maxLength: 6, buttonLabel: "Verify" },
  backup: {
    label: "Backup code",
    maxLength: 8,
    buttonLabel: "Use backup code"
  },
  bypass: {
    label: "Bypass code",
    maxLength: 24,
    buttonLabel: "Use bypass code"
  }
};

export default function MfaVerify() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mfaPending = useAppSelector(selectMfaPending);
  const isFetching = useAppSelector(selectAuthIsFetching);
  const errorMessage = useAppSelector(selectAuthErrorMessage);

  const [mode, setMode] = useState<VerifyMode>("code");
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [infoMessage, setInfoMessage] = useState("");

  // No pending challenge → nothing to verify here.
  useEffect(() => {
    if (!mfaPending) {
      navigate("/login", { replace: true });
    } else if (mfaPending.enrollmentRequired) {
      navigate("/mfa/enroll", { replace: true });
    }
  }, [mfaPending, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(
      () => setResendCooldown((seconds) => seconds - 1),
      1000
    );
    return () => clearInterval(timer);
  }, [resendCooldown]);

  if (!mfaPending || mfaPending.enrollmentRequired) return null;

  const isEmailOtp = mfaPending.method === "email_otp";
  const config = MODE_CONFIG[mode];
  const codeIsComplete =
    mode === "code" ? code.length === 6 : code.trim().length > 0;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!codeIsComplete || isFetching) return;
    dispatch(verifyMfa({ code: code.trim(), trustDevice, kind: mode }));
  };

  const handleResend = () => {
    setInfoMessage("");
    dispatch(resendMfaOtp())
      .unwrap()
      .then(() => {
        setInfoMessage("We sent you a new code.");
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch((rejection: { errorMessage?: string }) => {
        setInfoMessage(
          rejection?.errorMessage ??
            "Unable to resend the code. Please try again later."
        );
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      });
  };

  const switchMode = (nextMode: VerifyMode) => {
    setMode(nextMode);
    setCode("");
    setInfoMessage("");
    dispatch(clearState());
  };

  const backToSignIn = () => {
    dispatch(clearMfaPending());
    dispatch(clearState());
    navigate("/login", { replace: true });
  };

  const promptCopy =
    mode === "backup"
      ? "Enter one of the single-use backup codes you saved during enrollment."
      : mode === "bypass"
        ? "Enter the one-time bypass code your practice administrator generated for you."
        : isEmailOtp
          ? "We emailed you a 6-digit verification code. It expires in 10 minutes."
          : "Enter the 6-digit code from your authenticator app.";

  return (
    <form onSubmit={handleSubmit} data-testid="mfa-verify-page">
      <Stack gap={2}>
        <Typography variant="h5" fontWeight="bold">
          Two-step verification
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {promptCopy}
        </Typography>

        <TextField
          value={code}
          onChange={(event) => setCode(event.target.value)}
          label={config.label}
          autoFocus
          fullWidth
          autoComplete="one-time-code"
          inputProps={{
            maxLength: config.maxLength,
            inputMode: mode === "code" ? "numeric" : "text",
            "data-testid": "mfa-code-input",
            style: { letterSpacing: mode === "code" ? "0.5em" : undefined }
          }}
        />

        {mode === "code" && (
          <FormControlLabel
            control={
              <Checkbox
                checked={trustDevice}
                onChange={(event) => setTrustDevice(event.target.checked)}
              />
            }
            label="Trust this device for 30 days"
          />
        )}

        <LoadingButton
          type="submit"
          variant="contained"
          loading={isFetching}
          disabled={!codeIsComplete}
          data-testid="mfa-verify-submit"
        >
          {config.buttonLabel}
        </LoadingButton>

        {Boolean(errorMessage) && (
          <Alert icon={false} severity="error" data-testid="mfa-verify-error">
            {errorMessage}
          </Alert>
        )}
        {Boolean(infoMessage) && (
          <Alert icon={false} severity="info">
            {infoMessage}
          </Alert>
        )}

        <Stack gap={1}>
          {mode === "code" && isEmailOtp && (
            <Link
              component="button"
              type="button"
              underline="hover"
              disabled={resendCooldown > 0}
              onClick={handleResend}
              sx={{ width: "fit-content" }}
            >
              {resendCooldown > 0
                ? `Resend code (${resendCooldown}s)`
                : "Resend code"}
            </Link>
          )}
          {mode !== "code" ? (
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => switchMode("code")}
              sx={{ width: "fit-content" }}
            >
              Enter a verification code instead
            </Link>
          ) : (
            <>
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => switchMode("backup")}
                sx={{ width: "fit-content" }}
              >
                Use a backup code instead
              </Link>
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => switchMode("bypass")}
                sx={{ width: "fit-content" }}
              >
                Have a bypass code from your administrator?
              </Link>
            </>
          )}
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={backToSignIn}
            sx={{ width: "fit-content" }}
          >
            Back to sign in
          </Link>
        </Stack>
      </Stack>
    </form>
  );
}
